import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminContacts, saveContact } from "@/services/adminService";
import type { CrmContact, CrmStage } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import { useAdminCounters } from "@/pages/portal/admin/context/AdminCountersContext";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminStatCard from "@/pages/portal/admin/components/AdminStatCard";
import { adminCard, adminBtnGhost } from "@/pages/portal/admin/config/ui";
import { downloadCsv } from "@/utils/csv";

const STAGES: { key: CrmStage; label: string; bar: string }[] = [
  { key: "new",       label: "New",       bar: "bg-secondary-400" },
  { key: "contacted", label: "Contacted", bar: "bg-accent-400" },
  { key: "qualified", label: "Qualified", bar: "bg-primary-400" },
  { key: "proposal",  label: "Proposal",  bar: "bg-amber-400" },
  { key: "won",       label: "Won",       bar: "bg-emerald-400" },
  { key: "lost",      label: "Lost",      bar: "bg-rose-400" },
];

const fmt = (n: number) => n.toLocaleString("en-IN");
const initials = (name: string) =>
  name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2);

export default function CrmPipelineSection() {
  const { accessToken } = useAuth();
  const { refresh: refreshCounters } = useAdminCounters();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminContacts(accessToken),
    [] as CrmContact[],
    [accessToken],
  );
  const [busyId, setBusyId] = useState<number | null>(null);

  const summary = useMemo(() => {
    const open = data.filter((c) => c.stage !== "won" && c.stage !== "lost");
    const won = data.filter((c) => c.stage === "won");
    return {
      pipeline: open.reduce((s, c) => s + c.value, 0),
      won: won.reduce((s, c) => s + c.value, 0),
      openCount: open.length,
      wonCount: won.length,
    };
  }, [data]);

  async function move(contact: CrmContact, stage: CrmStage) {
    if (contact.stage === stage) return;
    setBusyId(contact.id);
    setData((prev) => prev.map((c) => (c.id === contact.id ? { ...c, stage } : c)));
    try {
      await saveContact(accessToken, { stage }, contact.id);
      refreshCounters();
    } catch {
      // Revert on failure
      setData((prev) => prev.map((c) => (c.id === contact.id ? { ...c, stage: contact.stage } : c)));
    } finally {
      setBusyId(null);
    }
  }

  function exportCsv() {
    downloadCsv(
      `sales-pipeline-${new Date().toISOString().slice(0, 10)}`,
      ["Name", "Company", "Email", "Phone", "Stage", "Deal Value", "Owner", "Source", "Last Activity"],
      data.map((c) => [c.name, c.company, c.email, c.phone ?? "", c.stage, c.value, c.owner, c.source, c.lastActivity]),
    );
  }

  if (!loading && error) return <AdminDataError message={error} onRetry={reload} />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Open Deals" value={loading ? "—" : fmt(summary.openCount)} icon="ri-flow-chart" accent="primary" />
        <AdminStatCard label="Open Pipeline Value" value={loading ? "—" : `₹${fmt(summary.pipeline)}`} icon="ri-funds-box-line" accent="accent" />
        <AdminStatCard label="Won Deals" value={loading ? "—" : fmt(summary.wonCount)} icon="ri-trophy-line" accent="emerald" />
        <AdminStatCard label="Won Value" value={loading ? "—" : `₹${fmt(summary.won)}`} icon="ri-money-rupee-circle-line" accent="secondary" />
      </div>

      {loading ? (
        <div className={`${adminCard} flex items-center justify-center py-16`}>
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {STAGES.map((stage) => {
            const cards = data.filter((c) => c.stage === stage.key);
            const total = cards.reduce((s, c) => s + c.value, 0);
            return (
              <div key={stage.key} className="flex w-64 shrink-0 flex-col rounded-xl border border-background-200 bg-background-100/60">
                <div className="flex items-center gap-2 border-b border-background-200 px-3.5 py-3">
                  <span className={`h-2.5 w-2.5 rounded-full ${stage.bar}`} />
                  <p className="text-xs font-bold uppercase tracking-wider text-foreground-700">{stage.label}</p>
                  <span className="ml-auto rounded-full bg-background-50 px-2 py-0.5 text-[11px] font-semibold text-foreground-600">
                    {cards.length}
                  </span>
                </div>
                <p className="px-3.5 pt-2 text-[11px] text-foreground-500">₹{fmt(total)} in stage</p>
                <div className="flex flex-1 flex-col gap-2.5 p-2.5">
                  {cards.length === 0 && (
                    <p className="rounded-lg border border-dashed border-background-300 py-6 text-center text-[11px] text-foreground-400">
                      No deals
                    </p>
                  )}
                  {cards.map((c) => (
                    <div key={c.id} className="rounded-lg border border-background-200 bg-background-50 p-3">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[10px] font-bold text-primary-700">
                          {initials(c.name)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-foreground-900">{c.name}</p>
                          <p className="truncate text-[10px] text-foreground-500">{c.company}</p>
                        </div>
                      </div>
                      <div className="mt-2.5 flex items-center justify-between">
                        <span className="text-xs font-bold tabular-nums text-foreground-950">₹{fmt(c.value)}</span>
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-800">
                          {c.source}
                        </span>
                      </div>
                      <select
                        value={c.stage}
                        disabled={busyId === c.id}
                        onChange={(e) => move(c, e.target.value as CrmStage)}
                        className="mt-2.5 h-7 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 text-[11px] text-foreground-700 outline-none focus:border-primary-400 disabled:opacity-50"
                        aria-label={`Move ${c.name} to stage`}
                      >
                        {STAGES.map((s) => (
                          <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="flex items-center gap-2 text-xs text-foreground-500">
        <i className="ri-information-line text-base" />
        Use the dropdown on any card to move a deal between stages — updates are saved instantly.
      </p>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={exportCsv}
          disabled={loading || data.length === 0}
          className={`${adminBtnGhost} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <i className="ri-download-2-line text-base" /> Export CSV
        </button>
        <button type="button" onClick={reload} className={adminBtnGhost}>
          <i className="ri-refresh-line text-base" /> Refresh board
        </button>
      </div>
    </div>
  );
}